import {httpClientV1, httpClientV2} from "../../../../infrastructure/http";
import {BankAccount} from "../../domain/bank-account.entity";
import {BankAccountMapperV2} from "../mappers/bank-account.mapper.v2";
import {
  V2StripeOnboardRequest,
  V2StripeOnboardResponse,
  V2StripeUpdateInitiateRequest,
  V2StripeUpdateInitiateResponse,
  V2StripeUpdateCompleteRequest,
  V2StripeUpdateCompleteResponse,
  V2StripeCompleteResponse,
  V2DisconnectResponse,
} from "../mappers/bank-account.mapper.types";

/**
 * Response from the Stripe login endpoint.
 */
export interface LoginToStripeResponse {
  data: {
    link: string;
  };
}

/**
 * Result from Stripe onboard operation.
 * Contains the bank account and optional login URL for Stripe Connect.
 */
export interface StripeOnboardResult {
  bankAccount: BankAccount;
  loginUrl: string | null;
}

/**
 * Repository for Stripe-specific bank account actions.
 * Handles onboarding, update, complete registration, and login flows.
 *
 * Pure TypeScript - no React imports.
 */
export class StripeRepository {
  /**
   * Onboard a new Stripe account.
   * Returns BankAccount with loginUrl.
   */
  async onboardStripe(params: V2StripeOnboardRequest): Promise<StripeOnboardResult> {
    const request: V2StripeOnboardRequest = {
      countryCode: params.countryCode,
    };
    const response = await httpClientV2.post<V2StripeOnboardResponse>(
      "payments/bank/stripe/onboard",
      request
    );
    const bankAccount = BankAccountMapperV2.toDomain(response.data);
    return {
      bankAccount,
      loginUrl: response.data.loginUrl ?? null,
    };
  }

  /**
   * Initiate Stripe account update (two-step flow).
   * Sends OTP to user's registered phone/email.
   */
  async initiateStripeUpdate(countryCode: string): Promise<void> {
    const request: V2StripeUpdateInitiateRequest = {countryCode};
    await httpClientV2.post<V2StripeUpdateInitiateResponse>(
      "payments/bank/stripe/update",
      request
    );
  }

  /**
   * Complete Stripe account update (two-step flow).
   * Called after user enters OTP from initiateStripeUpdate.
   */
  async completeStripeUpdate(otpCode: string): Promise<StripeOnboardResult> {
    const request: V2StripeUpdateCompleteRequest = {otpCode};
    const response = await httpClientV2.patch<V2StripeUpdateCompleteResponse>(
      "payments/bank/stripe/update/complete",
      request
    );
    const bankAccount = BankAccountMapperV2.toDomain(response.data);
    return {
      bankAccount,
      loginUrl: response.data.loginUrl ?? null,
    };
  }

  /**
   * Complete Stripe registration for inactive accounts.
   * Called after user returns from Stripe Connect onboarding.
   * @returns BankAccount with loginUrl
   */
  async completeStripeRegistration(): Promise<StripeOnboardResult> {
    const response = await httpClientV2.get<V2StripeCompleteResponse>(
      "payments/bank/stripe/complete"
    );
    const bankAccount = BankAccountMapperV2.toDomain(response.data);
    return {
      bankAccount,
      loginUrl: response.data.loginUrl ?? null,
    };
  }

  /**
   * Get Stripe login link for active accounts.
   * @returns Redirect link for Stripe dashboard
   */
  // Intentional V1 exception: no v2 endpoint for a fresh dashboard login link yet.
  async loginToStripe(): Promise<LoginToStripeResponse["data"]> {
    const response = await httpClientV1.get<LoginToStripeResponse>(
      "banks/accounts/login/eu"
    );
    return response.data;
  }

  /**
   * Disconnect payout account.
   * Disconnects any connected payout account (Paystack, Flutterwave, or Stripe).
   */
  async disconnectBankAccount(): Promise<void> {
    await httpClientV2.delete<V2DisconnectResponse>("payments/bank/disconnect");
  }
}

/**
 * Type guard to check if a result is a StripeOnboardResult.
 */
export function isStripeOnboardResult(
  result: StripeOnboardResult
): result is StripeOnboardResult {
  return "bankAccount" in result;
}
